/**
 * TestPageShell — Roomgrid frame shared by every channel test page.
 *
 * One flat 780px card (sections inside via <RgSection>), page title in the
 * topbar (react-admin <Title>), and a right-aligned near-black "Send test"
 * CTA in the footer, outside the card.
 */

import React from "react";
import { Box, Button, CircularProgress, Paper } from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import { Title, useTranslate } from "react-admin";

export interface TestPageShellProps {
  /** i18n key for the page title (topbar). */
  titleKey: string;
  submitting: boolean;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  children: React.ReactNode;
}

export const TestPageShell: React.FC<TestPageShellProps> = ({
  titleKey,
  submitting,
  onSubmit,
  children,
}) => {
  const translate = useTranslate();
  return (
    <>
      <Title title={titleKey} />
      <Box
        component="form"
        onSubmit={onSubmit}
        noValidate
        sx={{
          width: "100%",
          maxWidth: 780,
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          pb: 3,
        }}
      >
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          {children}
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={14} sx={{ color: "inherit" }} />
              ) : (
                <SendIcon sx={{ fontSize: 15 }} />
              )
            }
          >
            {translate("resources.test.actions.sendTest", { _: "Send test" })}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default TestPageShell;
